import Swiper from '@/shared/swiper-lite.js';
import {domLoader} from "@/loader.js";
import { initSteamStats } from '../components/steam-chart.js';

// live-streams-section (به‌همراه stream-play-tracker) قبلا به‌صورت ایستا
// import می‌شد، پس روی «هر» صفحهٔ محصول دانلود، پارس و کامپایل می‌شد —
// حتی محصولی که اصلا استریم ندارد. راه‌اندازی‌اش از قبل هم با
// requestIdleCallback عقب افتاده بود، پس تنبل کردن ماژول هیچ تغییری در
// زمان یا نحوهٔ ظاهر شدن بخش استریم ایجاد نمی‌کند.
let liveStreamsModulePromise = null;
// همین که ماژول resolve شد، به‌صورت همگام نگه داشته می‌شود تا مسیر
// destroy در livewire:navigating دقیقا مثل قبل همگام بماند.
let liveStreamsModuleReady = null;

function loadLiveStreamsModule() {
  if (!liveStreamsModulePromise) {
    // از طریق نمای lazy (توضیح در خودِ فایل) تا game-vods-page.js همراهش دانلود نشود.
    liveStreamsModulePromise = import('../shared/live-streams-section-lazy.js').then((module) => {
      liveStreamsModuleReady = module;
      return module;
    });
  }
  return liveStreamsModulePromise;
}

class ProductGallery {
  constructor() {
    this.currentIndex = 0;
    this.totalItems = document.querySelectorAll('.gallery-thumbnail').length;
    this.isOpen = false;
    this.currentVideo = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.minSwipeDistance = 50;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Touch/swipe support
    document.addEventListener('touchstart', (e) => this.handleTouchStart(e));
    document.addEventListener('touchend', (e) => this.handleTouchEnd(e));

    // Prevent body scroll when modal is open
    this.preventBodyScroll();
  }

  handleKeydown(e) {
    if (!this.isOpen) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.navigate('next'); // In RTL, left arrow should go to next
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.navigate('prev'); // In RTL, right arrow should go to previous
        break;
      case 'Home':
        e.preventDefault();
        this.goToFirst();
        break;
      case 'End':
        e.preventDefault();
        this.goToLast();
        break;
    }
  }

  handleTouchStart(e) {
    if (!this.isOpen) return;

    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
  }

  handleTouchEnd(e) {
    if (!this.isOpen) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = this.touchStartX - touchEndX;
    const diffY = this.touchStartY - touchEndY;

    // Only handle horizontal swipes
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > this.minSwipeDistance) {
      if (diffX > 0) {
        this.navigate('next');
      } else {
        this.navigate('prev');
      }
    }
  }

  preventBodyScroll() {
    const modal = document.getElementById('gallery-modal');
    if (!modal) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          if (modal.style.display === 'flex') {
            document.body.style.overflow = 'hidden';
          } else {
            document.body.style.overflow = '';
          }
        }
      });
    });

    observer.observe(modal, { attributes: true });
  }

  open(index) {
    if (index < 0 || index >= this.totalItems) return;

    this.currentIndex = index;
    this.isOpen = true;
    this.updateModal();
    this.showModal();
    this.updateThumbnailStates();
  }

  close() {
    this.isOpen = false;
    this.stopCurrentVideo();
    this.hideModal();
    this.updateThumbnailStates();
    document.body.style.overflow = '';
  }

  navigate(direction) {
    let newIndex;
    // Standard navigation logic (works for both LTR and RTL)
    if (direction === 'next') {
      newIndex = (this.currentIndex + 1) % this.totalItems;
    } else if (direction === 'prev') {
      newIndex = (this.currentIndex - 1 + this.totalItems) % this.totalItems;
    } else {
      return;
    }

    this.currentIndex = newIndex;
    this.updateModal();
    this.updateThumbnailStates();
  }

  goToFirst() {
    this.currentIndex = 0;
    this.updateModal();
    this.updateThumbnailStates();
  }

  goToLast() {
    this.currentIndex = this.totalItems - 1;
    this.updateModal();
    this.updateThumbnailStates();
  }

  updateModal() {
    const thumbnail = document.querySelector(`[data-index="${this.currentIndex}"]`);
    if (!thumbnail) return;

    const type = thumbnail.dataset.type;
    const path = thumbnail.dataset.path;
    const wrapper = document.getElementById('gallery-media-wrapper');

    // Stop current video
    this.stopCurrentVideo();

    // Update counter
    const currentIndexEl = document.getElementById('current-index');
    if (currentIndexEl) {
      currentIndexEl.textContent = this.currentIndex + 1;
    }

    // Clear previous content
    wrapper.innerHTML = '';

    // Create new media element
    const mediaElement = this.createMediaElement(type, path);
    wrapper.appendChild(mediaElement);

    // Update navigation buttons
    this.updateNavigationButtons();
  }

  createMediaElement(type, path) {
    let mediaElement;

    if (type === 'video') {
      mediaElement = document.createElement('video');
      mediaElement.src = path;
      mediaElement.controls = true;
      mediaElement.autoplay = true;
      mediaElement.style.width = '100%';
      mediaElement.style.height = 'auto';
      mediaElement.setAttribute('aria-label', 'Gallery video');

      // Store reference to current video
      this.currentVideo = mediaElement;

      // Handle video events
      mediaElement.addEventListener('loadstart', () => this.showLoading());
      mediaElement.addEventListener('canplay', () => this.hideLoading());
      mediaElement.addEventListener('error', () => this.hideLoading());

    } else {
      mediaElement = document.createElement('img');
      mediaElement.src = path;
      mediaElement.alt = 'Gallery image';
      mediaElement.style.width = '100%';
      mediaElement.style.height = 'auto';

      // Handle image loading
      mediaElement.addEventListener('loadstart', () => this.showLoading());
      mediaElement.addEventListener('load', () => this.hideLoading());
      mediaElement.addEventListener('error', () => this.hideLoading());
    }

    return mediaElement;
  }

  stopCurrentVideo() {
    if (this.currentVideo) {
      this.currentVideo.pause();
      this.currentVideo.currentTime = 0;
      this.currentVideo = null;
    }
  }

  updateThumbnailStates() {
    document.querySelectorAll('.gallery-thumbnail').forEach((thumb, index) => {
      thumb.classList.toggle('active', index === this.currentIndex);
    });
  }

  updateNavigationButtons() {
    const prevBtn = document.querySelector('.gallery-prev');
    const nextBtn = document.querySelector('.gallery-next');

    if (prevBtn) {
      // Never disable buttons - allow circular navigation
      prevBtn.disabled = false;
      prevBtn.setAttribute('aria-label', 'Previous image');
    }

    if (nextBtn) {
      // Never disable buttons - allow circular navigation
      nextBtn.disabled = false;
      nextBtn.setAttribute('aria-label', 'Next image');
    }
  }

  showLoading() {
    const wrapper = document.getElementById('gallery-media-wrapper');
    const loading = document.createElement('div');
    loading.className = 'gallery-loading';
    loading.textContent = 'Loading...';
    wrapper.appendChild(loading);
  }

  hideLoading() {
    const loading = document.querySelector('.gallery-loading');
    if (loading) {
      loading.remove();
    }
  }

  showModal() {
    const modal = document.getElementById('gallery-modal');
    modal.style.display = 'flex';

    // Add ARIA attributes for accessibility
    modal.setAttribute('aria-hidden', 'false');
    modal.setAttribute('aria-modal', 'true');

    requestAnimationFrame(() => {
      modal.classList.add('show');
    });

    // Focus management
    const closeBtn = document.querySelector('.gallery-close-btn-corner');
    if (closeBtn) {
      closeBtn.focus();
    }
  }

  hideModal() {
    const modal = document.getElementById('gallery-modal');
    modal.classList.remove('show');

    // Stop all videos when closing
    const videos = modal.querySelectorAll('video');
    videos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });

    // Remove ARIA attributes
    modal.setAttribute('aria-hidden', 'true');
    modal.removeAttribute('aria-modal');

    setTimeout(() => {
      modal.style.display = 'none';
    }, 300);
  }
}

window.productModuleInitialized=false
window.productGallerySwiperInstances = window.productGallerySwiperInstances || [];
window.productGallery = null;
const productReleaseCountdownTimers = new WeakMap();

function initProductReleaseCountdowns() {
  document.querySelectorAll('.product-release-countdown[data-release]').forEach((element) => {
    const existingInterval = productReleaseCountdownTimers.get(element);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    const releaseTime = new Date(element.getAttribute('data-release')).getTime();
    if (Number.isNaN(releaseTime)) {
      return;
    }

    const parts = {
      days: element.querySelector('[data-part="days"]'),
      hours: element.querySelector('[data-part="hours"]'),
      minutes: element.querySelector('[data-part="minutes"]'),
      seconds: element.querySelector('[data-part="seconds"]'),
    };

    if (!parts.days || !parts.hours || !parts.minutes || !parts.seconds) {
      return;
    }

    function updateCountdown() {
      const distance = releaseTime - Date.now();

      if (distance < 0) {
        parts.days.textContent = '00';
        parts.hours.textContent = '00';
        parts.minutes.textContent = '00';
        parts.seconds.textContent = '00';
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      parts.days.textContent = String(days).padStart(2, '0');
      parts.hours.textContent = String(hours).padStart(2, '0');
      parts.minutes.textContent = String(minutes).padStart(2, '0');
      parts.seconds.textContent = String(seconds).padStart(2, '0');
    }

    updateCountdown();
    productReleaseCountdownTimers.set(element, setInterval(updateCountdown, 1000));
  });
}

// Surprise discount timers (for .surprise-discount-timer[data-expire])
const surpriseDiscountTimers = new WeakMap();
function initSurpriseDiscountTimers() {
  document.querySelectorAll('.surprise-discount-timer[data-expire]').forEach((element) => {
    const existing = surpriseDiscountTimers.get(element);
    if (existing) {
      clearInterval(existing);
    }

    const expire = new Date(element.getAttribute('data-expire')).getTime();
    if (Number.isNaN(expire)) return;

    const parts = {
      days: element.querySelector('.sd-num[data-part="days"]') || element.querySelector('.sd-num[data-part="days"]'),
      hours: element.querySelector('.sd-num[data-part="hours"]'),
      minutes: element.querySelector('.sd-num[data-part="minutes"]'),
      seconds: element.querySelector('.sd-num[data-part="seconds"]'),
    };

    function update() {
      const now = Date.now();
      const diff = expire - now;
      if (diff <= 0) {
        parts.days && (parts.days.textContent = '00');
        parts.hours && (parts.hours.textContent = '00');
        parts.minutes && (parts.minutes.textContent = '00');
        parts.seconds && (parts.seconds.textContent = '00');
        return;
      }
      const seconds = Math.floor(diff / 1000) % 60;
      const minutes = Math.floor(diff / (1000 * 60)) % 60;
      const hours = Math.floor(diff / (1000 * 60 * 60)) % 24;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      parts.days && (parts.days.textContent = String(days).padStart(2,'0'));
      parts.hours && (parts.hours.textContent = String(hours).padStart(2,'0'));
      parts.minutes && (parts.minutes.textContent = String(minutes).padStart(2,'0'));
      parts.seconds && (parts.seconds.textContent = String(seconds).padStart(2,'0'));
    }

    update();
    surpriseDiscountTimers.set(element, setInterval(update, 1000));
  });
}

// Destroy همه swiper ها و gallery instance قبل از navigate
function destroyProductGallery() {
  // Destroy swiper instances
  if (window.productGallerySwiperInstances && window.productGallerySwiperInstances.length > 0) {
    window.productGallerySwiperInstances.forEach(swiper => {
      if (swiper && typeof swiper.destroy === 'function') {
        try {
          swiper.destroy(true, true);
        } catch (e) {
          console.warn('Error destroying product gallery swiper:', e);
        }
      }
    });
    window.productGallerySwiperInstances = [];
  }
  
  // Reset gallery instance
  if (window.productGallery) {
    window.productGallery = null;
  }
  if (window.gallery) {
    window.gallery = null;
  }
}

document.addEventListener('livewire:navigating', () => {
  window.productModuleInitialized=false;
  productTabState = null;
  // فقط اگر ماژول واقعا لود شده باشد چیزی برای destroy کردن وجود دارد.
  if (liveStreamsModuleReady) {
    liveStreamsModuleReady.destroyLiveStreamsSection('product-live-streams-mount');
  }
  destroyProductGallery();
});

document.addEventListener('livewire:navigated', () => {
  initProductReleaseCountdowns();
  initProductTabs();
});

document.addEventListener('livewire:init', () => {
  Livewire.hook('morph.updated', () => {
    initProductReleaseCountdowns();
    initSurpriseDiscountTimers();
  });
});


domLoader(function (){
  if (window.productModuleInitialized) {
    return;
  }
  window.productModuleInitialized = true;
  initProductsPage()
  // initialize custom surprise timers (if any)
  initSurpriseDiscountTimers();
})


function copyToClipboard(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand("copy");
  } catch (err) {
    console.error("خطا در کپی کردن متن:", err);
  }
  document.body.removeChild(textarea);
}

const PRODUCT_TAB_SLIDE_MS = 420;
let productTabState = null;
let productTabsClickBound = false;

function prefersReducedProductTabMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getProductTabPanel(name) {
  return document.getElementById('tab-' + name);
}

function setProductTabButtonsActive(tabs, activeIndex) {
  tabs.forEach((tab, index) => {
    tab.classList.toggle('active', index === activeIndex);
  });
}

function setProductTabPanelsActive(contents, activeIndex) {
  contents.forEach((panel, index) => {
    panel.classList.toggle('active', index === activeIndex);
  });
}

function clearProductTabPanelMotion(panel) {
  panel.classList.remove(
    'tab-content--entering',
    'tab-content--leaving',
    'tab-content--enter-from-start',
    'tab-content--enter-from-end',
    'tab-content--leave-to-start',
    'tab-content--leave-to-end'
  );
}

function switchProductTabInstant(tabs, contents, nextIndex) {
  setProductTabButtonsActive(tabs, nextIndex);
  contents.forEach(clearProductTabPanelMotion);
  setProductTabPanelsActive(contents, nextIndex);
}

function switchProductTabAnimated(stage, tabs, contents, currentIndex, nextIndex) {
  const leavingPanel = contents[currentIndex];
  const enteringPanel = contents[nextIndex];
  if (!leavingPanel || !enteringPanel || leavingPanel === enteringPanel) {
    return;
  }

  const forward = nextIndex > currentIndex;
  const enterClass = forward ? 'tab-content--enter-from-start' : 'tab-content--enter-from-end';
  const leaveClass = forward ? 'tab-content--leave-to-end' : 'tab-content--leave-to-start';

  setProductTabButtonsActive(tabs, nextIndex);

  stage.style.minHeight = `${leavingPanel.offsetHeight}px`;

  leavingPanel.classList.remove('active');
  leavingPanel.classList.add('tab-content--leaving', leaveClass);

  clearProductTabPanelMotion(enteringPanel);
  enteringPanel.classList.add('tab-content--entering', enterClass);

  let settled = false;
  const settle = () => {
    if (settled) {
      return;
    }
    settled = true;

    clearProductTabPanelMotion(leavingPanel);
    clearProductTabPanelMotion(enteringPanel);
    setProductTabPanelsActive(contents, nextIndex);

    requestAnimationFrame(() => {
      stage.style.minHeight = `${enteringPanel.offsetHeight}px`;
      requestAnimationFrame(() => {
        stage.style.minHeight = '';
      });
    });
  };

  enteringPanel.addEventListener('animationend', settle, { once: true });
  window.setTimeout(settle, PRODUCT_TAB_SLIDE_MS + 80);
}

function refreshProductTabState() {
  const root = document.querySelector('.cart-list.product-details');
  if (!root) {
    productTabState = null;
    return null;
  }

  const tabs = [...root.querySelectorAll('.tabs .tab[data-tab]')];
  const stage = root.querySelector('.product-tabs-stage');
  if (!tabs.length || !stage) {
    productTabState = null;
    return null;
  }

  const contents = tabs
    .map((tab) => getProductTabPanel(tab.getAttribute('data-tab')))
    .filter(Boolean);

  if (!contents.length) {
    productTabState = null;
    return null;
  }

  let currentIndex = tabs.findIndex((tab) => tab.classList.contains('active'));
  if (currentIndex < 0) {
    currentIndex = 0;
    setProductTabButtonsActive(tabs, currentIndex);
    setProductTabPanelsActive(contents, currentIndex);
  }

  productTabState = {
    tabs,
    contents,
    stage,
    currentIndex,
    isAnimating: false,
  };

  return productTabState;
}

function onProductTabClick(event) {
  const tab = event.target.closest('.cart-list.product-details .tabs .tab[data-tab]');
  if (!tab) {
    return;
  }

  if (!productTabState?.stage) {
    refreshProductTabState();
  }

  const state = productTabState;
  if (!state?.stage) {
    return;
  }

  const index = state.tabs.indexOf(tab);
  if (index < 0 || state.isAnimating || index === state.currentIndex) {
    return;
  }

  if (prefersReducedProductTabMotion()) {
    switchProductTabInstant(state.tabs, state.contents, index);
    state.currentIndex = index;
    return;
  }

  state.isAnimating = true;
  switchProductTabAnimated(state.stage, state.tabs, state.contents, state.currentIndex, index);
  state.currentIndex = index;
  window.setTimeout(() => {
    state.isAnimating = false;
  }, PRODUCT_TAB_SLIDE_MS + 100);
}

function initProductTabs() {
  refreshProductTabState();

  if (!productTabsClickBound) {
    document.addEventListener('click', onProductTabClick);
    productTabsClickBound = true;
  }

  return productTabState;
}

function initProductsPage(){
  initProductTabs();

  // Copy to clipboard function

  // Add click handler for product link
  addClickHandler(".pl", function (event) {
    let result = document.getElementById('product-link').getAttribute('data-link')
    copyToClipboard(result)
    document.getElementById('text-x-c').innerHTML = 'کپی شد'
  })

  // Swiper for product gallery
  let productGallerySwiper = null;
  if (document.querySelector(".product-gallery-swiper")) {
    productGallerySwiper = new Swiper(".product-gallery-swiper", {
    on: {
      click(swiper, event) {
        const target = event.target.closest('a[wire\\:navigate]');
        if (target) {
          const simulatedClick = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          target.dispatchEvent(simulatedClick);
        }
      }
    },
    spaceBetween: 8,
    pagination: {
      el: ".swiper-pagination",
      clickable: true,
    },
    breakpoints: {
      0: {
        slidesPerView: 1.5,
        spaceBetween: 8,
      },
      440: {
        slidesPerView: 2.5,
        spaceBetween: 8,
      },
      570: {
        slidesPerView: 2.7,
        spaceBetween: 8,
      },
      640: {
        slidesPerView: 3.1,
        spaceBetween: 8,
      },
      768: {
        slidesPerView: 4,
        spaceBetween: 8,
      },
      900: {
        slidesPerView: 5,
        spaceBetween: 8,
      },
      1024: {
        slidesPerView: 6,
        spaceBetween: 12,
      },
      1300: {
        slidesPerView: 5.8,
        spaceBetween: 12,
      },
    },
  });
    if (productGallerySwiper && window.productGallerySwiperInstances) {
      window.productGallerySwiperInstances.push(productGallerySwiper);
    }
  }

  let gallery = null;


  // Make functions globally available
  window.openGallery = openGallery;
  window.closeGallery = closeGallery;
  window.navigateGallery = navigateGallery;
  window.searchInCountry = searchInCountry;
  window.showEditionsLoading = showEditionsLoading;

  // Initialize when DOM is ready
  computePercet()
  calculateLineWidth()

  window.Livewire.on("price-changed", function () {
    computePercet()
  })

  // Recalculate on window resize
  window.addEventListener('resize', calculateLineWidth);

  // Initialize suggestion products
  initSuggestionProducts();
  initProductReleaseCountdowns();
  scheduleProductLiveStreams();
  initPriceChartDialog();
  initSteamStats();

  // Initialize Swiper for gallery thumbnails
  if (document.querySelector('.gallery-swiper')) {
    const gallerySwiper = new Swiper('.gallery-swiper', {
      slidesPerView: 'auto',
      spaceBetween: 12,
      freeMode: true,
      grabCursor: true,
      direction: 'horizontal',
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: true,
      },
      touchRatio: 1,
      touchAngle: 45,
      threshold: 5,
      breakpoints: {
        0: {
          slidesPerView: 1.2,
          spaceBetween: 5,
        },
        320: {
          slidesPerView: 1.8,
          spaceBetween: 12,
          centeredSlides: false,
        },
        480: {
          slidesPerView: 2.2,
          spaceBetween: 12,
        },
        603: {
          slidesPerView: 2.8,
          spaceBetween: 12,
        },
        768: {
          slidesPerView: 4,
          spaceBetween: 12,
        },
        1024: {
          slidesPerView: 5,
          spaceBetween: 12,
        },
        1921: {
          slidesPerView: 6,
          spaceBetween: 12,
        }

      }
    });
    if (gallerySwiper && window.productGallerySwiperInstances) {
      window.productGallerySwiperInstances.push(gallerySwiper);
    }
  }

  // Only initialize if gallery elements exist
  if (document.querySelector('.gallery-thumbnail') && !gallery && !window.gallery) {
    gallery = new ProductGallery();
    window.gallery = gallery;
    window.productGallery = gallery;
  }

  // Event delegation for gallery thumbnails
  document.addEventListener('click', function(e) {
    if (e.target.closest('.gallery-thumbnail')) {
      const thumbnail = e.target.closest('.gallery-thumbnail');
      const index = parseInt(thumbnail.dataset.index);
      openGallery(index);
    }
  });

  // Event delegation for navigation buttons
  document.addEventListener('click', function(e) {
    if (e.target.closest('.gallery-nav-btn[data-direction]')) {
      const button = e.target.closest('.gallery-nav-btn[data-direction]');
      const direction = button.dataset.direction;
      navigateGallery(direction);
    }
  });

  // Event delegation for close button
  document.addEventListener('click', function(e) {
    if (e.target.closest('#gallery-close-btn')) {
      closeGallery();
    }
  });

  // Document click listener for closing gallery
  document.addEventListener('click', function (e) {
    if (e.target.id === 'gallery-modal' ||
      e.target.classList.contains('gallery-modal-content') ||
      e.target.classList.contains('gallery-media-container')) {

      // Stop all videos when closing
      const modal = document.getElementById('gallery-modal');
      const videos = modal.querySelectorAll('video');
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });

      modal.classList.remove('show');
      setTimeout(() => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      }, 300);
    }
  });
}




function initSuggestionProducts() {
  let productBoxSwiper = document.getElementsByClassName('product-box-2-container')
  if (productBoxSwiper.length > 0) {
    for (let i = 0; i < productBoxSwiper.length; i++) {
      let container = productBoxSwiper[i]
      container.classList.remove('wojs')
      let swiper = container.querySelector('.swiper')
      if (!swiper) {
        continue;
      }

      let id = swiper.getAttribute('id')

      new Swiper("#" + id, {
        on: {
          click(swiper, event) {
            const target = event.target.closest('a[wire\\:navigate]');
            if (target) {
              const simulatedClick = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
              });
              target.dispatchEvent(simulatedClick);
            }
          }
        },
        slidesPerView: 5,
        spaceBetween: 8,
        freeMode: true,
        breakpoints: {
          0: {
            slidesPerView: 1.2,
            spaceBetween: 5,
          },
          320: {
            slidesPerView: 1.8,
            spaceBetween: 5,
          },
          420: {
            slidesPerView: 2.2,
            spaceBetween: 6,
          },
          550: {
            slidesPerView: 2.8,
            spaceBetween: 8,
          },
          620: {
            slidesPerView: 3.6,
            spaceBetween: 8,
          },
          769: {
            slidesPerView: 4.3,
            spaceBetween: 8,
          },
          1024: {
            slidesPerView: 5.2,
            spaceBetween: 8,
          },
        },
      });
    }
  }
}
function calculateLineWidth() {
  const nameEnElement = document.querySelector('.name-en .n-en');
  const lineElement = document.querySelector('.name-en .line');

  if (nameEnElement && lineElement) {
    // Create a temporary element to measure text width
    const tempElement = document.createElement('span');
    tempElement.style.visibility = 'hidden';
    tempElement.style.position = 'absolute';
    tempElement.style.whiteSpace = 'nowrap';
    tempElement.style.fontSize = window.getComputedStyle(nameEnElement).fontSize;
    tempElement.style.fontFamily = window.getComputedStyle(nameEnElement).fontFamily;
    tempElement.style.fontWeight = window.getComputedStyle(nameEnElement).fontWeight;
    tempElement.style.letterSpacing = window.getComputedStyle(nameEnElement).letterSpacing;
    tempElement.textContent = nameEnElement.textContent;

    document.body.appendChild(tempElement);
    const textWidth = tempElement.offsetWidth;
    document.body.removeChild(tempElement);

    // Calculate line width (container width - text width - padding)
    const containerWidth = nameEnElement.parentElement.offsetWidth;
    const paddingLeft = parseInt(window.getComputedStyle(nameEnElement).paddingLeft) || 0;

    // Check if we're on mobile and adjust padding
    const isMobile = window.innerWidth <= 768;
    const mobilePadding = isMobile ? 12 : 0; // 12px for mobile (left: 12px !important)

    const lineWidth = containerWidth - textWidth - paddingLeft - mobilePadding;

    // Set the line width
    lineElement.style.width = Math.max(0, lineWidth) + 'px';
  }
}

function computePercet() {
  let discount = document.getElementById("price-discount")
  if (discount) {
    // Discount element found
  }
}

function openGallery(index) {
  if (!window.gallery && !window.productGallery) {
    const newGallery = new ProductGallery();
    window.gallery = newGallery;
    window.productGallery = newGallery;
  }
  const gallery = window.gallery || window.productGallery;

  // Add click animation for video thumbnails
  const thumbnail = document.querySelector(`[data-index="${index}"]`);
  if (thumbnail && thumbnail.dataset.type === 'video') {
    thumbnail.classList.add('video-clicked');
    setTimeout(() => {
      thumbnail.classList.remove('video-clicked');
    }, 600);
  }

  gallery.open(index);
}

function closeGallery() {

  // Stop all videos in the gallery
  const videos = document.querySelectorAll('#gallery-modal video');
  videos.forEach(video => {
    video.pause();
    video.currentTime = 0;
  });

  if (window.gallery) {
    window.gallery.close();
  } else {
  }
}

function navigateGallery(direction) {
  if (window.gallery) {
    window.gallery.navigate(direction);
  }
}

// Country search functions
function searchInCountry() {
  let keyword = document.getElementById("country-keyword").value;
  let countryRows = document.getElementsByClassName("country-row");

  for (let i = 0; i < countryRows.length; i++) {
    let row = countryRows[i];
    let innerHTML = row.innerHTML;
    let text = innerHTML;
    let camelParts = getCompressedCamelParts(text);
    let compressed = text.toLowerCase().replace(/\s+/g, "");
    let matches = compressed.includes(keyword) || startsWithAbbreviation(camelParts, keyword);
    row.style.display = matches ? "" : "none";
  }
}

function getCompressedCamelParts(str) {
  let parts = str.match(/[A-Z]?[a-z]+/g) || [];
  return parts.map(part => part.toLowerCase());
}

function startsWithAbbreviation(parts, keyword) {
  let abbrev = "";
  for (let part of parts) {
    abbrev += part;
    if (keyword.startsWith(abbrev.slice(0, keyword.length))) return true;
  }

  let compressed = "";
  for (let part of parts) {
    compressed += part.slice(0, Math.max(1, Math.ceil(keyword.length / parts.length)));
  }
  return compressed.startsWith(keyword);
}

// Show editions loading function
function showEditionsLoading(element) {
  const spinner = element.querySelector('.spinner');
  const content = element.querySelector('.editions-content');

  if (spinner && content) {
    spinner.style.display = 'flex';
    content.style.display = 'none';
    element.style.pointerEvents = 'none';
  }
}

// ---- دیالوگ نمودار تغییرات قیمت -------------------------------------------
// دکمه‌ی بازکننده داخل کامپوننت livewire (pricing-extra) است و ممکن است
// re-render شود؛ پس با event delegation کار می‌کنیم و فقط یک‌بار bind می‌کنیم.
let priceChartLoadedSrc = null;
let priceChartDelegationBound = false;

function getPriceChartModal() {
  // با @teleport به body منتقل شده؛ سراسری پیدایش می‌کنیم (ممکن است Alpine
  // آن را کمی بعد از init منتقل کند، پس هر بار تازه کوئری می‌کنیم).
  return document.querySelector('[data-price-chart-modal]');
}

function openPriceChart(baseSrc) {
  const modal = getPriceChartModal();
  if (!baseSrc || !modal) return;

  const frame = modal.querySelector('.price-chart-modal__frame');

  // تم فعلی سایت را همراه iframe می‌فرستیم. اگر تم عوض شده باشد (یا بار اول
  // باشد) iframe را دوباره با تمِ درست لود می‌کنیم تا رنگ متن با مودال هماهنگ
  // بماند؛ در غیر این صورت لود مجدد نمی‌شود تا پلاگین چارت بی‌جهت لود نشود.
  const theme = document.body.classList.contains('dark') ? 'dark' : 'light';
  const src = baseSrc + (baseSrc.includes('?') ? '&' : '?') + 'theme=' + theme;

  if (priceChartLoadedSrc !== src) {
    frame.src = src;
    priceChartLoadedSrc = src;
  }

  modal.hidden = false;
  document.body.style.overflow = 'hidden';
}

function closePriceChart() {
  const modal = getPriceChartModal();
  if (!modal) return;
  modal.hidden = true;
  document.body.style.overflow = '';
}

function initPriceChartDialog() {
  // مودال را به body منتقل می‌کنیم تا backdrop روی کل صفحه بیفتد،
  // نه محدود به سکشنِ دارای transform (کارت سه‌بعدی تصویر).
  const modal = document.querySelector('[data-price-chart-modal]');
  if (modal && modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }
  priceChartLoadedSrc = null;

  // delegation فقط یک‌بار bind می‌شود (روی document باقی می‌ماند).
  if (priceChartDelegationBound) return;
  priceChartDelegationBound = true;

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-price-chart-trigger]');
    if (trigger) {
      openPriceChart(trigger.getAttribute('data-src'));
      return;
    }
    if (e.target.closest('[data-price-chart-close]')) {
      closePriceChart();
    }
  });

  document.addEventListener('keydown', (e) => {
    const modal = getPriceChartModal();
    if (e.key === 'Escape' && modal && !modal.hidden) {
      closePriceChart();
    }
  });
}

function scheduleProductLiveStreams() {
  // initLiveStreamsSection هم بدون این المان بلافاصله return می‌کرد؛
  // این شرط فقط جلوی دانلود شدن چانک را می‌گیرد.
  if (!document.getElementById('product-live-streams-mount')) {
    return;
  }

  loadLiveStreamsModule().then(({ scheduleLiveStreamsSection }) => scheduleLiveStreamsSection({
    mountId: 'product-live-streams-mount',
    slotId: 'product-live-streams-player-slot',
    wrapId: 'product-live-streams-player',
    qualityStorageKey: 'pls-quality',
    swiperLayout: 'responsive',
    loadingSkeleton: true,
    richCards: true,
    onClose: () => {
      document.querySelectorAll('#product-live-streams-mount .product-live-streams__card').forEach((item) => {
        item.classList.remove('is-active');
      });
    },
  }));
}

